<template>
    <b-modal id="edit-discrepancy-report" title="Discrepancy Reconciliation" ref="modal" ok-title="Save" @ok="handleOk"
        @hidden="onReset" :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons"
        :no-close-on-backdrop="true" centered size="xxl">
        <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

        <b-row>
            <b-col sm="2">
                <b-form-group label="Select Month">
                    <v-select class="style-chooser" label="text" placeholder=" - Please select - " v-model="form.month"
                        :disabled="true">
                    </v-select>
                </b-form-group>
            </b-col>
            <b-col sm="5">
                <b-form-group label="Source">
                    <v-select class="style-chooser" label="text" placeholder=" - Please select - " v-model="form.source"
                        :reduce="(source) => source.value" :disabled="true">
                    </v-select>
                </b-form-group>
            </b-col>
            <b-col sm="5">
                <b-form-group label="Destination">
                    <v-select class="style-chooser" label="text" placeholder=" - Please select - "
                        v-model="form.destination" :reduce="(destination) => destination.value" :disabled="true">
                    </v-select>
                </b-form-group>
            </b-col>
        </b-row>

        <b-table show-empty striped hover :items="items" :fields="fields" :current-page="currentPage"
            :per-page="perPage" :filter="filter" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection" responsive>
            <template v-slot:cell(dispatchNo)="row">
                <span class="numFont">
                    {{ row.item.dispatchNo }}
                </span>
            </template>
            <template v-slot:cell(source)="row">
                {{ row.item["Source"] }}
            </template>
            <template v-slot:cell(destination)="row">
                {{ row.item["Destination"] }}
            </template>
            <template v-slot:cell(discrepancies)="row">
                <div class="numFont">
                    <b-form-input v-model.number="form.totalDiscrepancies" type="number" name="TotalDiscrepancies"
                        size="sm" v-validate="discrepancyRule" class="text-center" @keypress="preventInvalidKey" />
                    <span v-show="errors.has('TotalDiscrepancies')" class="help-block">
                        Reconciliation count must be within discrepancy total
                    </span>
                </div>
            </template>
            <template v-slot:cell(currentTotal)="row">
                {{ row.item.currentTotal }}
            </template>
            <template v-slot:cell(adjustedTotal)="row">
                {{ adjustedTotal }}
            </template>
        </b-table>

        <b-row>
            <b-col sm="6">
                <b-form-group class="mt-2" label="Remarks*" label-class="font-weight-bold pt-0" label-for="Remarks">
                    <b-form-textarea name="Remarks" type="text" maxlength="200" v-model="form.remarks" v-validate="{
                        required: true,
                        regex: remarksRegex,
                    }" :rows="3" placeholder="Remarks" />
                    <span v-show="errors.has('Remarks')" class="help-block">
                        {{ errors.first("Remarks") }}
                    </span>
                </b-form-group>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script>
// API
import discrepancyReportApi from "@/api/discrepancyReportApi";

// Util
import { DiscrepancyReportUtil } from "@/utils/discrepancyReportUtil";

// Others
import EventBus from '@/shared/event-bus';
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import moment from "moment";
import _ from 'lodash';

export default {
    name: 'edit-discrepancy-report',
    components: {
        Loading,
    },
    data() {
        return {
            form: {
                remarks: '',
                totalDiscrepancies: 1,
            },
            items: [],
            fields: [
                {
                    key: 'dispatchNo',
                    label: 'Dispatch No.',
                    sortable: true,
                },
                {
                    key: 'Source',
                    sortable: true,
                    thClass: 'align-content-center',
                },
                {
                    key: 'Destination',
                    sortable: true,
                    thClass: 'align-content-center',
                },
                {
                    key: 'Discrepancies',
                    label: 'Total Discrepancies',
                    sortable: true,
                    tdClass: 'text-center',
                },
                {
                    key: 'currentTotal',
                    label: 'Current Total',
                    sortable: true,
                },
                {
                    key: 'adjustedTotal',
                    label: 'Adjusted Total',
                    sortable: true,
                },
            ],

            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageOptions: [5, 10, 15, 25, 50, 100],
            sortBy: null,
            sortDesc: false,
            sortDirection: 'asc',
            filter: null,

            selDiscrepancyReport: {},

            currUserId: this.$store.getters.loggedUser.id,

            // Check for loader
            isLoading: false,
        };
    },
    mounted() {
        EventBus.$on('onUpdateDiscrepancyReport', (discrepancyReport) => {
            this.selDiscrepancyReport = discrepancyReport;

            this.form.month = moment(discrepancyReport.dateDeployed).format('MMMM');
            this.form.source = discrepancyReport.Source;
            this.form.destination = discrepancyReport.Destination;
            this.form.totalDiscrepancies = discrepancyReport.Discrepancies;

            const asset = _.find(this.selDiscrepancyReport.assets,
                x => x.assetTypeId === discrepancyReport.assetTypeId);

            if (asset) {
                this.form.remarks = asset.remarks;
            }

            this.items.push({ ...discrepancyReport });

            this.totalRows = this.items.length;
        });
    },
    computed: {
        total() {
            return `Total Discrepancies: ${this.selDiscrepancyReport.Discrepancies}`;
        },
        adjustedTotal() {
            return Number(this.form.totalDiscrepancies) + Number(this.selDiscrepancyReport.currentTotal);
        },
        disableConfirmButtons() {
            return this.isLoading;
        },
        dispatchNo() {
            return this.selDiscrepancyReport.dispatchNo;
        },
        remarksRegex() {
            return config.remarksRegex;
        },
        discrepancyRule() {
            const max = this.selDiscrepancyReport !== null ? this.selDiscrepancyReport.Discrepancies : 1;
            return `required|discrepancyRange:1,${max}`;
        }
    },
    methods: {
        preventInvalidKey(event) {
            const invalidChars = ['e', 'E', '+', '-', '.'];
            if (invalidChars.includes(event.key)) {
                event.preventDefault();
            }
        },
        generateDiscrepancyReport() {
            const assetArray = this.selDiscrepancyReport.assets;
            assetArray.forEach(asset => {
                if (asset.assetTypeId === this.selDiscrepancyReport.assetTypeId) {
                    asset.discrepancies = (asset.discrepancies || 0) + this.form.totalDiscrepancies;
                    asset.addedDiscrepancies = this.form.totalDiscrepancies;
                    asset.remarks = this.form.remarks;
                }
            });

            const discrepancyReport = DiscrepancyReportUtil.cleanupFields(this.selDiscrepancyReport);

            return {
                ...discrepancyReport,
            };
        },
        getParam() {
            const discrepancyReport = this.generateDiscrepancyReport();

            return {
                currUserId: this.currUserId,
                discrepancyReportArr: [discrepancyReport],
            };
        },
        async handleOk(evt) {
            // Prevent modal from closing
            evt.preventDefault();

            const isValid = await this.$validator.validateAll();
            if (!isValid) {
                this.$toaster.warning("Please address the field/s with invalid input.");
                return;
            }

            await this.processDiscrepancyReport();
        },
        async processDiscrepancyReport() {
            // show loading indicator
            this.isLoading = true;

            try {
                const param = this.getParam();
                const { data } = await discrepancyReportApi.editDiscrepancyReport(param);

                if (data.isSuccess) {
                    this.$toaster.success(data.message);
                    this.$refs.modal.hide();
                    EventBus.$emit("onCloseEditDiscrepancyReport", data.discrepancyReportArr);
                } else {
                    this.$toaster.warning(data.message);
                }
            } catch (_error) {
                this.$toaster.error(
                    `Error updating Discrepancy Report "${this.dispatchNo}". Please try again.`
                );
            }

            // hide loading indicator
            this.isLoading = false;
        },
        onReset() {
            this.form = {
                month: this.selDiscrepancyReport.dateDeployed
                    ? moment(this.selDiscrepancyReport.dateDeployed).format("MMMM")
                    : '',
                source: this.selDiscrepancyReport.Source || '',
                destination: this.selDiscrepancyReport.Destination || '',
                remarks: this.selDiscrepancyReport.remarks || '',
                totalDiscrepancies: 1
            };

            this.selDiscrepancyReport = {};

            this.items = [];
            this.totalRows = 0;

            // reset validation
            this.$validator.reset();
            this.errors.clear();
        },
    },
    beforeDestroy() {
        EventBus.$off("onUpdateDiscrepancyReport");
    },
};
</script>